import { AxiosError } from 'axios';
import { ErrorResponse } from '../model/response/error-response.model';
import HTTPUtils from './http.util';
import FlashMessage from '../components/flash-message.component';
import ErrorCdMsgConstants from '../constants/error-cd-msg.constants';
export default class ErrorMessageUtils {
  /**
   * ビルドエラーメッセージ。
   *
   * @param errCd エラーコード
   * @param errMsg エラーメッセージ
   * @returns フォーマットされたエラーコードとエラーメッセージ
   */
  static buildErrorMessage(errCd: string, errMsg: string): string {
    if (!errCd || !errMsg) {
      return '';
    }
    // エラーメッセージがすでにエラーコードを内包している場合、そのまま返す
    if (errMsg.includes(errCd)) {
      return errMsg;
    }
    return '【' + errCd + '】' + errMsg;
  }

  /**
   * ネットワークオフラインメッセージの構築。
   * @returns ネットワーク切断時のエラーメッセージ
   */
  static buildNetworkOfflineMsg(): string {
    return this.buildErrorMessage(
      ErrorCdMsgConstants.FUNC_NETWORK_PROXY_SERVER_CONNECTION_ERR_CD,
      ErrorCdMsgConstants.FUNC_NETWORK_PROXY_SERVER_CONNECTION_ERR_MSG
    );
  }

  /**
   * サーバー切断表示チェック。
   *
   * @param {AxiosError<ErrorResponse>} error エラー応答
   * @param {boolean} displayCloseIconForServerErrorFlag サーバーエラーフラグのクローズアイコン表示
   * @returns true: サーバーの切断, false: サーバー接続
   */
  static async checkToShowDisconnectMsg(
    error: AxiosError<ErrorResponse>,
    displayCloseIconForServerErrorFlag: boolean
  ): Promise<boolean> {
    if (this.checkToShowOfflineMsg()) {
      return true;
    }

    return await this.checkToShowServerDisconnectMsg(error, displayCloseIconForServerErrorFlag);
  }

  /**
   * オフラインメッセージ表示チェック。
   *
   * @returns true: オフライン, false: オンライン
   */
  static checkToShowOfflineMsg(): boolean {
    if (!navigator.onLine) {
      FlashMessage.getInstance().showNetworkOfflineMsg();
      return true;
    }
    return false;
  }

  /**
   * サーバー切断表示チェック。
   *
   * @param {AxiosError<ErrorResponse>} error エラー応答
   * @param {boolean} displayCloseIconForServerErrorFlag サーバーエラーフラグのクローズアイコン表示
   * @returns true: サーバーの切断, false: サーバー接続
   */
  static async checkToShowServerDisconnectMsg(
    error: AxiosError<ErrorResponse>,
    displayCloseIconForServerErrorFlag: boolean
  ): Promise<boolean> {
    const serverDisconnectionMsg = this.getServerDisconnectMsg(error);
    if (serverDisconnectionMsg) {
      await FlashMessage.getInstance().showDisconnectMsg(serverDisconnectionMsg, displayCloseIconForServerErrorFlag);
      return true;
    }
    return false;
  }

  /**
   * 入力文字列内のすべてのプレースホルダーを置換配列の値に置き換えます。
   *
   * @param inputString 入力文字列
   * @param replacement プレースホルダーの置換値の配列
   * @returns 置換配列の値で置換されたプレースホルダーを含む入力文字列
   */
  static replacePlaceholder(inputString: string, replacement: string[]) {
    if (!inputString || !replacement || replacement.length <= 0) {
      return inputString;
    }

    return inputString.replace(/%s/g, () => replacement.shift() || '');
  }

  /**
   * サーバー切断ゲッター。
   *
   * @param {AxiosError<ErrorResponse>} error エラー応答
   * @returns エラーメッセージ文字列またはnull
   */
  static getServerDisconnectMsg(error: AxiosError<ErrorResponse>): string | null {
    if (!error) {
      return null;
    }
    // Nginxに接続できない、またはバックエンドに接続できない
    const responseData = error?.response?.data as unknown;
    if (
      error.message?.includes('Network Error') ||
      HTTPUtils.is504Error(error) ||
      HTTPUtils.is502Error(error) ||
      (process.env.NODE_ENV === 'development' &&
        ((HTTPUtils.is500Error(error) && typeof responseData === 'string' && responseData.includes('Proxy error')) ||
          error.message?.includes('timeout of')))
    ) {
      return this.buildErrorMessage(
        ErrorCdMsgConstants.FUNC_BACKEND_CONNECTION_ERR_CD,
        ErrorCdMsgConstants.FUNC_BACKEND_CONNECTION_ERR_MSG
      );
    }

    return null;
  }
}
